import { FunctionalComponent, h } from 'preact'
import { useContext, useEffect, useState } from 'preact/hooks'
import cn from 'classnames'
import { Svg } from 'components/ui'
import BaseContext from 'contexts/base-context'
import { postMessage } from 'scripts/utils'
import { MessageActions } from 'scripts/hooks/useReceiveMessages'
import { ScreenshotDescription, TriggerEvent } from 'scripts/type'

import styles from './index.scss'
import { useTranslation, useUploadAttachment } from 'scripts/hooks'

type Props = {
  fieldCode: string
  onUploadStart?: () => void
  onUploadEnd?: (data: string) => void
}

const Screenshots: FunctionalComponent<Props> = (props) => {
  const { fieldCode, onUploadStart, onUploadEnd } = props
  const { urlToken } = useContext(BaseContext)
  const [capturing, setCapturing] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [captured, setCaptured] = useState(false)
  const uploadAttachment = useUploadAttachment()
  const { captured: capturedText, screenshot } = (useTranslation()('screenshotDescription') ||
    {}) as ScreenshotDescription
  const uploadingEnd = (fileKey: string) => {
    setUploading(false)
    onUploadEnd?.(fileKey)
  }

  useEffect(() => {
    uploading && onUploadStart?.()
  }, [uploading])

  useEffect(() => {
    const handCaptureSelected = (e: Event) => {
      const blob = (e as CustomEvent).detail.blob
      const currentFieldCode = (e as CustomEvent).detail.fieldCode
      if (currentFieldCode !== fieldCode) {
        return
      }
      if (blob) {
        setUploading(true)
        setCaptured(true)
        const file = new File([blob!], 'Screenshot', {
          type: blob.type,
          lastModified: Date.now(),
        })
        uploadAttachment({
          file,
          urlToken,
          fieldCode,
          onSuccess: uploadingEnd,
          onFail: () => uploadingEnd(''),
        })
      } else {
        setCaptured(false)
        uploadingEnd('')
      }
    }

    const handleCaptured = () => setCapturing(false)

    window.addEventListener(MessageActions.WINDOW_CAPTURE_SELECTED, handCaptureSelected)
    window.addEventListener(MessageActions.WINDOW_CAPTURED, handleCaptured)

    return () => {
      window.removeEventListener(MessageActions.WINDOW_CAPTURE_SELECTED, handCaptureSelected)
      window.removeEventListener(MessageActions.WINDOW_CAPTURED, handleCaptured)
    }
  }, [])

  const handleClickCapture = () => {
    setCapturing(true)
    postMessage({
      name: TriggerEvent.CaptureWindowStart,
      data: { fieldCode, campaignToken: urlToken },
    })
  }

  return (
    <a onClick={handleClickCapture} className={cn(styles.wrapper, { [styles.done]: captured })}>
      <Svg.Screenshot /> {captured ? capturedText : screenshot}
      {(uploading || capturing) && <Svg.Loader className={styles.loader} />}
    </a>
  )
}

export default Screenshots
